import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { apiClient } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AppHeader from '../components/layout/AppHeader';
import ClientPortalTour from '../components/onboarding/ClientPortalTour';
import DashboardCards from '../components/client/DashboardCards';
import SmartMatchingPanel from '../components/client/SmartMatchingPanel';
import PostOpportunityButton from '../components/client/PostOpportunityButton';
import PostOpportunityModal from '../components/client/PostOpportunityModal';
import ProjectsTab from '../components/client/tabs/ProjectsTab';
import FinancialsTab from '../components/client/tabs/FinancialsTab';
import ActivityFeedTab from '../components/client/tabs/ActivityFeedTab';
import OverviewTab from '../components/client/tabs/OverviewTab';

const ClientPortal = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  const [projects, setProjects] = useState<any[]>([]);
  const [dashboardMetrics, setDashboardMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const [projectsData, metricsData] = await Promise.all([
          apiClient.getProjects(),
          apiClient.getClientDashboardMetrics()
        ]);

        const projectsArray = Array.isArray(projectsData) ? projectsData : (projectsData?.data || projectsData?.projects || []);
        setProjects(projectsArray);
        setDashboardMetrics(metricsData);
      } catch (err) {
        console.error('Failed to load client data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchClientData();
    }
  }, [user]);

  // Derived stats
  const safeProjects = Array.isArray(projects) ? projects : [];
  const activeProjects = safeProjects.filter((p: any) => !p.status || p.status === 'active' || p.status === 'in_progress');
  // Financial data from real transactions
  const financials = dashboardMetrics?.recentTransactions || [];

  // Extended mocked & backend stats for DashboardCards
  const projectsCount = dashboardMetrics?.activeProjects || 0;

  // Real progress
  const totalProgress = activeProjects.reduce((acc: number, p: any) => acc + (p.progress || 0), 0);
  const completionPercent = activeProjects.length > 0 ? Math.round(totalProgress / activeProjects.length) : 0;

  // Real risk based on backend calculation
  const riskStatus = dashboardMetrics?.atRiskProjects > 0 ? '🟡 At Risk' : (dashboardMetrics?.activeProjects > 0 ? '🟢 On Track' : '🔴 No Active Projects');

  const totalAllocated = dashboardMetrics?.escrowAllocated || 0;
  const spentSoFar = dashboardMetrics?.escrowSpent || 0;
  const escrowLocked = (totalAllocated - spentSoFar);
  const escrowAvailable = 0; // The logic for escrow shouldn't invent available funds if we don't know the client's bank account

  const totalHired = dashboardMetrics?.talentHired || 0;
  const activeHires = dashboardMetrics?.activeTalent || 0;

  const tasksCompletedThisWeek = dashboardMetrics?.velocityTasksCompleted || 0;
  const tasksVelocityPercent = dashboardMetrics?.velocityPercent;
  const avgTurnaroundHours = dashboardMetrics?.velocityAvgTurnaround || 'N/A';

  const paymentReliability = dashboardMetrics?.paymentReliability || 10.0;
  const communicationRating = dashboardMetrics?.communicationRating || 10.0;
  const disputeRate = dashboardMetrics?.disputeRate || 0;
  const aiSuggestion = dashboardMetrics?.spendingOptimizationInsight || '';

  return (
    <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-x-hidden relative">
      <ClientPortalTour />
      {/* Wood Texture Background */}
      <div
        className="absolute inset-0 z-0 opacity-40 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=2670&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          filter: 'contrast(1.2) brightness(0.8)'
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />

      {/* Header */}
      <AppHeader />

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 pt-12 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-[1400px] mx-auto flex flex-col xl:flex-row gap-8">

          {/* Left/Main Column */}
          <div className="flex-1 min-w-0">
            <div className="mb-8">
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-premium-gold-light via-premium-gold to-premium-gold-dark drop-shadow-lg mb-4">
                Client Command Center
              </h1>
              <p className="text-lg text-white/60">Overview of your projects, resources, and team synergy.</p>
            </div>

            {/* Stats Overview */}
            <DashboardCards
              projectsCount={projectsCount}
              completionPercent={completionPercent}
              riskStatus={riskStatus}
              totalAllocated={totalAllocated}
              escrowLocked={escrowLocked}
              escrowAvailable={escrowAvailable}
              spentSoFar={spentSoFar}
              totalHired={totalHired}
              activeHires={activeHires}
              tasksCompletedThisWeek={tasksCompletedThisWeek}
              tasksVelocityPercent={tasksVelocityPercent}
              avgTurnaroundHours={avgTurnaroundHours}
              paymentReliability={paymentReliability}
              communicationRating={communicationRating}
              disputeRate={disputeRate}
              aiSuggestion={aiSuggestion}
            />

            {/* Tabs */}
            <div id="client-tabs" className="flex gap-4 border-b border-white/10 mb-8 overflow-x-auto pb-2">
              {['overview', 'projects', 'financials', 'activity'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 font-medium text-sm uppercase tracking-wider transition-colors whitespace-nowrap ${activeTab === tab
                    ? 'text-premium-gold border-b-2 border-premium-gold shadow-[0_4px_10px_-4px_rgba(212,175,55,0.5)]'
                    : 'text-white/40 hover:text-white'
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div id="client-content" className="min-h-[400px]">
              {loading && (
                <div className="text-center py-24">
                  <div className="w-12 h-12 border-4 border-premium-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <div className="text-white/60">Loading client data...</div>
                </div>
              )}

              {!loading && activeTab === 'overview' && (
                <OverviewTab activeProjects={activeProjects} financials={financials} dashboardMetrics={dashboardMetrics} />
              )}

              {!loading && activeTab === 'projects' && (
                <ProjectsTab projects={projects} />
              )}

              {!loading && activeTab === 'financials' && (
                <FinancialsTab financials={financials} />
              )}

              {!loading && activeTab === 'activity' && (
                <ActivityFeedTab activities={dashboardMetrics?.activities} />
              )}

            </div>
          </div>

          {/* Right Column - Smart Matching */}
          <div className="hidden xl:block w-80 shrink-0">
            <SmartMatchingPanel suggestions={dashboardMetrics?.suggestedTalent} />
          </div>

        </motion.div>

        {/* Global Post Opportunity Button */}
        <PostOpportunityButton onClick={() => setIsPostModalOpen(true)} />

        {/* Post Opportunity Modal */}
        <PostOpportunityModal
          isOpen={isPostModalOpen}
          onClose={() => setIsPostModalOpen(false)}
          onSubmit={(data) => {
            console.log('Posting new opportunity:', data);
            setIsPostModalOpen(false);
            // Optionally show a toast here to simulate the AI matching trigger
            import('react-hot-toast').then(({ default: toast }) => {
              toast.success('Opportunity posted! AI is scanning for top matches.', {
                icon: '🤖',
                style: { background: '#2C1810', color: '#D4AF37', border: '1px solid #D4AF37' }
              });
            });
          }}
        />
      </main>
    </div>
  );
};

export default ClientPortal;
